import type { OnboardingAnswers, Question } from "../types/onboarding";

interface AnalyticsHandler {
	logEvent: (
		eventName: string,
		value?: string | number,
		metadata?: Record<string, unknown>,
	) => void;
}

export class OnboardingAnalyticsService {
	private analytics: AnalyticsHandler;

	constructor(analytics: AnalyticsHandler) {
		this.analytics = analytics;
	}

	trackScreenViewed(
		currentScreenIndex: number,
		question: Question,
		totalScreens: number,
	) {
		// Log specific events for each screen (easier to find in analytics)
		if (currentScreenIndex === 0) {
			this.analytics.logEvent("onboarding_first_screen", undefined, {
				field_name: question.field,
				total_screens: totalScreens.toString(),
			});
		} else if (currentScreenIndex === 1) {
			this.analytics.logEvent("onboarding_second_screen", undefined, {
				field_name: question.field,
				total_screens: totalScreens.toString(),
			});
		} else if (currentScreenIndex === 2) {
			this.analytics.logEvent("onboarding_third_screen", undefined, {
				field_name: question.field,
				total_screens: totalScreens.toString(),
			});
		} else if (currentScreenIndex === 3) {
			this.analytics.logEvent("onboarding_fourth_screen", undefined, {
				field_name: question.field,
				total_screens: totalScreens.toString(),
			});
		} else if (currentScreenIndex === 4) {
			this.analytics.logEvent("onboarding_fifth_screen", undefined, {
				field_name: question.field,
				total_screens: totalScreens.toString(),
			});
		} else if (currentScreenIndex === 5) {
			this.analytics.logEvent("onboarding_sixth_screen", undefined, {
				field_name: question.field,
				total_screens: totalScreens.toString(),
			});
		} else if (currentScreenIndex === 6) {
			this.analytics.logEvent("onboarding_seventh_screen", undefined, {
				field_name: question.field,
				total_screens: totalScreens.toString(),
			});
		} else if (currentScreenIndex === 7) {
			this.analytics.logEvent("onboarding_eighth_screen", undefined, {
				field_name: question.field,
				total_screens: totalScreens.toString(),
			});
		}

		this.analytics.logEvent("onboarding_screen_viewed", undefined, {
			screen_index: currentScreenIndex.toString(),
			field_name: question.field,
			total_screens: totalScreens.toString(),
			is_first_screen: (currentScreenIndex === 0).toString(),
		});
	}

	trackNextClicked(
		question: Question,
		currentScreenIndex: number,
		isLastScreen: boolean,
		hasAnswer: boolean,
		totalScreens: number,
	) {
		this.analytics.logEvent("onboarding_next_clicked", undefined, {
			field_name: question.field,
			question_index: currentScreenIndex.toString(),
			is_last_screen: isLastScreen.toString(),
			has_answer: hasAnswer.toString(),
			total_screens: totalScreens.toString(),
		});

		// Track when user skips the additional-info screen without entering anything
		if (question.type === "additional-info" && !hasAnswer) {
			this.analytics.logEvent("additional_info_skipped", undefined, {
				field_name: question.field,
				screen_index: currentScreenIndex.toString(),
			});
		}
	}

	trackSkipClicked(
		question: Question,
		currentScreenIndex: number,
		totalScreens: number,
	) {
		this.analytics.logEvent("onboarding_skip_clicked", undefined, {
			field_name: question.field,
			question_index: currentScreenIndex.toString(),
			total_screens: totalScreens.toString(),
			screens_remaining: (totalScreens - currentScreenIndex - 1).toString(),
		});
	}

	trackBackClicked(currentScreenIndex: number, totalScreens: number) {
		this.analytics.logEvent("onboarding_back_clicked", undefined, {
			previous_screen_index: currentScreenIndex.toString(),
			total_screens: totalScreens.toString(),
		});
	}

	trackAbandoned(currentScreenIndex: number, totalScreens: number) {
		this.analytics.logEvent("onboarding_abandoned", undefined, {
			last_screen_index: currentScreenIndex.toString(),
			total_screens: totalScreens.toString(),
			completion_percentage: (
				(currentScreenIndex / totalScreens) *
				100
			).toFixed(1),
		});
	}

	trackCompleteClicked(answers: OnboardingAnswers, totalScreens: number) {
		const completedFields = Object.keys(answers).filter(
			(key) => answers[key] && answers[key] !== "" && answers[key] !== null,
		);

		this.analytics.logEvent("onboarding_complete_clicked", undefined, {
			total_fields_completed: completedFields.length.toString(),
			completed_fields: completedFields.join(","),
			total_screens: totalScreens.toString(),
		});
	}

	trackCompletedSuccessfully(answers: OnboardingAnswers, totalScreens: number) {
		const completedFields = Object.keys(answers).filter(
			(key) => answers[key] && answers[key] !== "" && answers[key] !== null,
		);

		this.analytics.logEvent("onboarding_completed_successfully", undefined, {
			total_fields_completed: completedFields.length.toString(),
			completed_fields: completedFields.join(","),
			total_screens: totalScreens.toString(),
		});
	}

	trackCompletionError(error: unknown, answers: OnboardingAnswers) {
		const completedFields = Object.keys(answers).filter(
			(key) => answers[key] && answers[key] !== "" && answers[key] !== null,
		);

		this.analytics.logEvent("onboarding_completion_error", undefined, {
			error_message: error instanceof Error ? error.message : "unknown",
			total_fields_completed: completedFields.length.toString(),
		});
	}

	trackContinueToEra(reason: string) {
		this.analytics.logEvent("onboarding_continue_to_era_clicked", undefined, {
			reason,
		});
	}
}
